import React, { useCallback, useMemo } from "react";
import {
	CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import { DOUBLE_RESPONSIVE_CONTAINER_MIN_SIZE } from "../../constants/constants";
import { useAppSelector } from "../../store/hooks/hooks";
import useMediaQuery from "../../store/hooks/useMediaQuery";

type ChartPoint = {
	x: number;
	y?: number;
	yVLP?: number;
	yIPR?: number;
	yCritical?: number;
};

type NumericResponseValue = number | string;

const dictToLine = (
	xValues?: Record<string, NumericResponseValue>,
	yValues?: Record<string, NumericResponseValue>,
	yKey: keyof ChartPoint = "y"
) => {
	if (!xValues || !yValues) {
		return [];
	}

	return Object.keys(xValues).flatMap(key => {
		const x = Number(xValues[key]);
		const y = Number(yValues[key]);
		return Number.isFinite(x) && Number.isFinite(y) ? [{ x, [yKey]: y }] : [];
	});
}

const pointToData = (point?: { x: NumericResponseValue, y: NumericResponseValue }) => {
	if (!point) {
		return [];
	}

	const x = Number(point.x);
	const y = Number(point.y);
	return Number.isFinite(x) && Number.isFinite(y) ? [{ x, y }] : [];
}

const SystemPlotChart = ({ wellId }: { wellId: string }) => {
	const isMobile = useMediaQuery("(max-width: 900px)");
	const chartsData = useAppSelector(state => wellId === "well1" ? state.systemPlot.chartsData1 : state.systemPlot.chartsData2);
	const results = useAppSelector(state => wellId === "well1" ? state.systemPlot.result1 : state.systemPlot.result2);

	const vlpData = useMemo(
		() => dictToLine(chartsData?.[0]?.x_vlp_ipr, chartsData?.[0]?.y_vlp, "yVLP"),
		[chartsData]
	);
	const iprData = useMemo(
		() => dictToLine(chartsData?.[0]?.x_vlp_ipr, chartsData?.[0]?.y_ipr, "yIPR"),
		[chartsData]
	);
	const criticalData = useMemo(
		() => dictToLine(chartsData?.[1]?.x_critical, chartsData?.[1]?.y_critical, "yCritical"),
		[chartsData]
	);
	const point0 = useMemo(() => pointToData(results?.point0), [results?.point0]);
	const point1 = useMemo(() => pointToData(results?.point1), [results?.point1]);

	const allPoints = useMemo<ChartPoint[]>(
		() => [...vlpData, ...iprData, ...criticalData, ...point0, ...point1],
		[vlpData, iprData, criticalData, point0, point1]
	);
	const isEmpty = allPoints.length === 0;

	const { minX, maxX, minY, maxY, xTicks, yTicks } = useMemo(() => {
		if (isEmpty) {
			const minX = 0, maxX = 700, minY = 0, maxY = 350;
			const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
			const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
			return { minX, maxX, minY, maxY, xTicks, yTicks };
		}

		const xValues = allPoints.map(point => point.x).filter(Number.isFinite);
		const yValues = allPoints
			.flatMap(point => [point.y, point.yVLP, point.yIPR, point.yCritical])
			.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
		const minX = Math.min(...xValues);
		const maxX = Math.max(...xValues);
		const minY = Math.min(...yValues);
		const maxY = Math.max(...yValues);
		const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
		const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
		return { minX, maxX, minY, maxY, xTicks, yTicks };
	}, [allPoints, isEmpty]);

	const renderLegend = useCallback((props: { payload?: readonly any[] }) => {
		return (
			<ul className={`system-plot__legend${isMobile ? " system-plot__legend--mobile" : ""}`}>
				{props.payload?.map((entry, index) => (
					<li key={index} className="system-plot__legend-item">
						<svg width="28" height="14" style={{ flexShrink: 0 }}>
							<line
								x1={0} y1={7} x2={28} y2={7}
								stroke={entry.color}
								strokeWidth={entry.payload?.strokeWidth ?? 2}
								strokeDasharray={entry.payload?.strokeDasharray}
							/>
						</svg>
						<span>{entry.value}</span>
					</li>
				)) ?? []}
			</ul>
		);
	}, [isMobile]);

	return (
		<div className={`system-plot__chart-container  ${wellId}`}>
			<ResponsiveContainer width="100%" height={300} {...DOUBLE_RESPONSIVE_CONTAINER_MIN_SIZE}>
				<LineChart
					data={isEmpty ? [{ x: minX, y: minY }] : []}
					margin={{
						top: 20,
						right: 30,
						left: 45,
						bottom: 55
					}}>

					<CartesianGrid
						strokeDasharray="0"
						horizontalValues={yTicks}
						verticalValues={xTicks}
					/>
					<XAxis
						axisLine={true}
						dataKey="x"
						domain={[minX, maxX]}
						label={{ value: "Q", position: "insideBottom", offset: -10 }}
						type="number"
						ticks={xTicks}
						tickFormatter={(value: number) => value.toFixed(0)}
					/>
					<YAxis
						axisLine={true}
						domain={[minY, maxY]}
						ticks={yTicks}
						label={{ value: "P", angle: -90, position: "insideLeft", offset: -28 }}
						tickFormatter={(value: number) => value > 1000 ? value.toExponential(2) : value.toFixed(2)}
					/>
					<Tooltip wrapperStyle={{ pointerEvents: "none" }} isAnimationActive={false} />
					<Legend
						content={renderLegend}
						layout={isMobile ? "horizontal" : "vertical"}
						verticalAlign={isMobile ? "bottom" : "top"}
						align={isMobile ? "center" : "right"}
					/>
					<Line type="monotone" data={vlpData} dataKey="yVLP" stroke="#4c23deff" strokeWidth={3} name="VLP" dot={true} activeDot={false} legendType="plainline" />
					<Line type="monotone" data={iprData} dataKey="yIPR" stroke="#e8732aff" strokeWidth={3} name="IPR" dot={false} activeDot={false} legendType="plainline" />
					<Line type="monotone" data={criticalData} dataKey="yCritical" stroke="#2a9d8f" strokeWidth={3} name="Critical" dot={false} activeDot={false} legendType="plainline" />
					<Line type="monotone" data={point0} dataKey="y" stroke="#e63946" strokeWidth={0} name="  " dot={true} activeDot={false} />
					<Line type="monotone" data={point1} dataKey="y" stroke="#000000" strokeWidth={0} name=" " dot={true} activeDot={false} />
					{[...point0, ...point1].map((point, index) => (
						<React.Fragment key={`point-projection-${index}`}>
							<ReferenceLine
								segment={[{ x: point.x, y: minY }, point]}
								strokeDasharray="4 4"
								stroke={index === 0 ? "#e63946" : "#000000"}
								label={{ value: point.x.toFixed(2), position: "bottom", offset: 24, color: index === 0 ? "#e63946" : "#000000" }}
							/>
							<ReferenceLine
								segment={[{ x: minX, y: point.y }, point]}
								strokeDasharray="4 4"
								stroke={index === 0 ? "#e63946" : "#000000"}
								label={{ value: point.y.toFixed(2), position: "left", offset: 54, color: index === 0 ? "#e63946" : "#000000" }}
							/>
						</React.Fragment>
					))}
				</LineChart>
			</ResponsiveContainer>
		</div>
	)
}

export default React.memo(SystemPlotChart);
